import { MOUNT_ADD, REGISTER_FRAME } from '../generated/protocol';
import { Emitter } from '../utils/emitter';
import * as logger from '../utils/logger';
import { IInitConfig } from './message-types';

/**
 * A message bus to handle messaging with the parent
 * used when this code is ran inside the iframe
 * */
export class IFrameParentMessageBus {
  private parentId: number | null = null;
  private messageId = 0;

  private messageEmitter = new Emitter();
  onMessage = this.messageEmitter.event;

  private fsPort: MessagePort | null = null;
  private fsPortResolvers: Array<(port: MessagePort) => void> = [];

  private babelPort: MessagePort | null = null;
  private babelPortResolvers: Array<(port: MessagePort) => void> = [];

  private initConfig: IInitConfig | null = null;
  private initConfigResolvers: Array<(config: IInitConfig) => void> = [];

  /** First-wins latch for `register-frame` (R3-352). */
  private registered = false;
  /** How many window messages we dropped for coming from somewhere other than the
   *  parent. Counted rather than logged per-event: a hostile sibling can post in a
   *  loop, and an unbounded `console.error` would hand it a log-flood DoS. */
  private foreignMessageCount = 0;

  constructor() {
    this._messageListener = this._messageListener.bind(this);

    window.addEventListener('message', this._messageListener);
  }

  /** How many window-level messages arrived from a window that is not the parent
   *  (R3-352). Exposed for the adversarial tests and for host-side diagnostics —
   *  a non-zero value means some other frame is probing this one. */
  get droppedForeignMessages(): number {
    return this.foreignMessageCount;
  }

  private _messageListener(evt: MessageEvent) {
    // HOST_ORIGIN_HARDENING_SPEC §2.4 (R3-352): a message is trusted by WHICH
    // WINDOW it came from, never by its content. Any iframe on the page can reach
    // any other through `window.parent.frames[i]` and `postMessage` cross-origin,
    // so without this check a hostile app in one frame could forge `register-frame`
    // (swapping this frame's fs/babel ports for its own), `mount-add` (shadowing
    // `/app` with an attacker-served filesystem), `fs-change` (forcing the
    // recompile that evaluates it) or `refresh` (a reload loop) into a PRIVILEGED
    // sibling frame — and the attacker's code would then run inside that frame's
    // realm, holding that frame's grants. Every legitimate window→frame message in
    // this protocol is posted by the sandpack client in the host document, i.e. by
    // `window.parent`; the fs/babel/mount `MessagePort`s ride inside those same
    // messages, so nothing legitimate is lost.
    //
    // This is the frame-side half of the identity rule. The parent side has always
    // had its own (`evt.source !== this.frameWindow`, sandpack-client
    // `iframe-protocol.ts`); this half was never built.
    if (evt.source !== window.parent) {
      this.foreignMessageCount++;
      // Debug level on purpose — see `foreignMessageCount`. The counter, not the
      // log line, is the durable signal.
      logger.debug('ignoring window message from a non-parent source', evt.data && evt.data.type);
      return;
    }

    const data = evt.data;

    if (data && data.type === REGISTER_FRAME) {
      // Single-shot (R3-352). `register-frame` transfers this frame's fs and babel
      // ports and settles `initConfig`; honoring a second one would re-fire the
      // resolvers and swap live ports underneath the running bundler. First wins —
      // for a forged duplicate AND for a benign parent re-render race, which is why
      // this is a latch rather than a source check alone.
      //
      // Legitimate re-registration does not go through here: every path that gives
      // this frame a new client NAVIGATES the iframe first (the sandpack client's
      // constructor and its `refresh` dispatch both call `setLocationURLIntoIFrame`,
      // and `refresh` reloads the document), which builds a FRESH bus with the latch
      // clear. A duplicate reaching a live bus is therefore a host bug or a forgery,
      // and either way honoring it is worse than refusing it: the pre-R3-352 code
      // half-swapped (already-drained resolvers keep the OLD ports while `fsPort`,
      // `initConfig` and `parentId` become the NEW ones).
      if (this.registered) {
        logger.warn('ignoring duplicate register-frame; this frame is already registered');
        // Close the ports we are not going to adopt rather than leaving the sender
        // with a channel that silently never answers — a refused handshake should
        // fail fast, not hang (ways_of_working §3).
        for (const orphan of evt.ports ?? []) {
          try {
            orphan.close();
          } catch {
            /* already closed */
          }
        }
        return;
      }
      this.registered = true;
      this.parentId = data.id;

      // Bootstrap config rides on the same handshake message that transfers the
      // fs port. `register-frame` is handled before the `data.codesandbox` guard
      // and the parent omits that flag here, so read the fields directly.
      const config: IInitConfig = {
        template: data.template,
        logLevel: data.logLevel,
        // Forwarded verbatim by the sandpack client's `register({...config})`
        // (no fork change needed); present only once the host wires delivery.
        sdkIntegrity: data.sdkIntegrity,
        // The §5.2 dirty set (writable-layer + journal-deleted repo-relative
        // paths); absent until the host wires delivery.
        dirtyPaths: data.dirtyPaths,
        // The §5.7 distrust mark for this (coords, commitSha).
        distrustArtifacts: data.distrustArtifacts,
        // R3-49b batch-hydration snapshot of the mounted tree; absent until the
        // host wires delivery.
        fsSnapshot: data.fsSnapshot,
        // The chrome region this frame occupies (R3-114); absent for standalone /
        // older hosts. Forwarded verbatim by the sandpack client's `register(...)`.
        region: data.region,
      };
      this.initConfig = config;
      const configResolvers = this.initConfigResolvers;
      this.initConfigResolvers = [];
      for (const resolve of configResolvers) {
        resolve(config);
      }

      // The parent transfers two ports on this handshake: ports[0] is the fs
      // RPC port, ports[1] (when present) connects to the parent-owned Babel
      // worker. The Babel worker moved to the parent so the iframe sandbox can
      // drop `allow-same-origin` (a same-origin worker could no longer load
      // from an opaque-origin iframe).
      const port = evt.ports && evt.ports[0];
      if (port) {
        port.start();
        this.fsPort = port;
        const resolvers = this.fsPortResolvers;
        this.fsPortResolvers = [];
        for (const resolve of resolvers) {
          resolve(port);
        }
      }

      const babelPort = evt.ports && evt.ports[1];
      if (babelPort) {
        babelPort.start();
        this.babelPort = babelPort;
        const resolvers = this.babelPortResolvers;
        this.babelPortResolvers = [];
        for (const resolve of resolvers) {
          resolve(babelPort);
        }
      }
      return;
    }

    if (!data || !data.codesandbox) {
      return;
    }

    // `mount-add` transfers a MessagePort for the new mount's filesystem. The
    // generic message channel drops `evt.ports`, so attach them here for the
    // mount handler to pick up. (`register-frame` above is handled separately.)
    if (data.type === MOUNT_ADD && evt.ports && evt.ports.length > 0) {
      this.messageEmitter.fire({ ...data, ports: evt.ports });
      return;
    }

    this.messageEmitter.fire(data);
  }

  /**
   * Resolves with the `MessagePort` transferred by the parent during `register-frame`.
   * The parent side is expected to have called zenfs's `RPC.attach` / `attachFS` on this port.
   */
  getFsPort(): Promise<MessagePort> {
    if (this.fsPort) {
      return Promise.resolve(this.fsPort);
    }
    return new Promise((resolve) => {
      this.fsPortResolvers.push(resolve);
    });
  }

  /**
   * Resolves with the `MessagePort` transferred by the parent during
   * `register-frame` that connects to the parent-owned Babel worker. The
   * `BabelTransformer` uses it as its `WorkerMessageBus` endpoint instead of
   * constructing its own `Worker` (which it can't, on an opaque origin).
   */
  getBabelPort(): Promise<MessagePort> {
    if (this.babelPort) {
      return Promise.resolve(this.babelPort);
    }
    return new Promise((resolve) => {
      this.babelPortResolvers.push(resolve);
    });
  }

  /**
   * Resolves with the bootstrap config (template/logLevel/recompileDelay)
   * delivered by the parent during `register-frame`.
   */
  getInitConfig(): Promise<IInitConfig> {
    if (this.initConfig) {
      return Promise.resolve(this.initConfig);
    }
    return new Promise((resolve) => {
      this.initConfigResolvers.push(resolve);
    });
  }

  _postMessage(message: any) {
    // HOST_ORIGIN_HARDENING_SPEC §2.4 (MessagePort identity rule): this frame
    // runs at an opaque origin, so `targetOrigin='*'` is correct *by design* —
    // an opaque-origin frame has no concrete origin to name, and identity is
    // established by the source window + the transferred MessagePort handshake
    // (the `register-frame` ports above), not by the origin string. Do NOT
    // "harden" this to a concrete origin; it would break the handshake. See
    // CODE_SPEC_REFERENCES.md.
    window.parent.postMessage(message, '*');
  }

  sendMessage(type: string, data: Record<string, any> = {}) {
    this._postMessage({
      ...data,
      $id: this.parentId,
      type,
      codesandbox: true,
    });
  }

  protocolRequest(protocolName: string, method: string, params: Array<any>): Promise<any> {
    const type = `protocol-${protocolName}`;
    const messageId = this.messageId++;
    return new Promise((resolve, reject) => {
      const disposable = this.onMessage((msg: any) => {
        if (msg.msgId === messageId && msg.type === type && !msg.method) {
          disposable.dispose();

          if (msg.error) {
            reject(new Error(msg.error.message));
          } else {
            resolve(msg.result);
          }
        }
      });

      this.sendMessage(type, {
        msgId: messageId,
        method: method,
        params: params,
      });
    });
  }
}
